import React, { useState, useEffect } from 'react';
import CancelLogin from './CancelLogin';
import { preConsentFormSubmissions } from '../actions';

// Sanitization functions
const sanitizeString = (input = '', format = false) => {
    if (!input.trim()) return '';
    let sanitized = input.trim().replace(/[^a-zA-Z\s-'.]/g, '');
    sanitized = sanitized.replace(/\s+/g, ' ');
    return format ? sanitized.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : sanitized;
};

const sanitizeEmail = (email = '') => {
    if (!email.trim()) return '';
    return email.trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '');
};

const PreConsentForm = (props) => {
    const parsedData = JSON.parse(props.jsonData);
    const { fields, acknowledgement } = parsedData.form;

    
    const [formValues, setFormValues] = useState({
        given_name: '',
        surname: '',
        email: ''
    });

    const [formErrors, setFormErrors] = useState(props.preConsentFormData.errors);

    // Update form values when preconsentFormData becomes available
    useEffect(() => {
        if (props.preConsentFormData) {
            setFormValues({
                given_name: props.preConsentFormData.given_name || '',
                surname: props.preConsentFormData.surname || '',
                email: props.preConsentFormData.email || ''
            });
        }
    }, [props.preConsentFormData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const errors = [];

        const { given_name, surname, email } = formValues;

        // Create a validation config based on form fields
        const validationConfig = fields.reduce((config, field) => {
            config[field.name] = {
                maxLength: field.maxLength || 50,
                label: field.label
            };
            return config;
        }, {});

        const isValidNameFormat = (name) => /^[a-zA-Z\s]+$/.test(name.trim());
        const isValidNameLength = (name, maxLength) => name.trim().length <= maxLength;
        const isValidEmailFormat = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());

        const invalidFormatGiven = given_name && !isValidNameFormat(given_name);
        const invalidFormatSurname = surname && !isValidNameFormat(surname);

        if (!given_name && !surname) {
            errors.push("Given Name and Surname are required.");
        } else {
            if (!given_name) errors.push("Given Name is required.");
            if (!surname) errors.push("Surname is required.");

            if (invalidFormatGiven && invalidFormatSurname) {
                errors.push("Only alphabets and spaces are allowed in Given Name and Surname.");
            } else if (invalidFormatGiven) {
                errors.push("Only alphabets and spaces are allowed in Given Name.");
            } else if (invalidFormatSurname) {
                errors.push("Only alphabets and spaces are allowed in Surname.");
            }
        }

        const longGiven = given_name && !isValidNameLength(given_name, validationConfig.given_name?.maxLength || 50);
        const longSurname = surname && !isValidNameLength(surname, validationConfig.surname?.maxLength || 50);
        const longEmail = email && !isValidNameLength(email, validationConfig.email?.maxLength || 100);

        if (longGiven && longSurname) {
            errors.push(`Maximum of 50 characters allowed in Given Name and Surname.`);
            //errors.push(`Maximum of ${validationConfig.given_name?.maxLength || 50} characters allowed in ${validationConfig.given_name?.label || 'Given Name'} and ${validationConfig.surname?.maxLength || 50} characters allowed in ${validationConfig.surname?.label || 'Surname'}.`);
        } else if (longGiven) {
            errors.push(`Maximum of ${validationConfig.given_name?.maxLength || 50} characters allowed in ${validationConfig.given_name?.label || 'Given Name'}.`);
        } else if (longSurname) {
            errors.push(`Maximum of ${validationConfig.surname?.maxLength || 50} characters allowed in ${validationConfig.surname?.label || 'Surname'}.`);
        }

        if (!email) {
            errors.push("Email is required.");
        } else if (!isValidEmailFormat(email)) {
            errors.push("Not a valid email format.");
        } else if (longEmail) {
            errors.push(`Maximum of ${validationConfig.email?.maxLength || 100} characters allowed in ${validationConfig.email?.label || 'Email'}.`);
        }

        if (errors.length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors([]);

        const sanitizedData = {
            given_name: sanitizeString(given_name, true),
            surname: sanitizeString(surname, true),
            email: sanitizeEmail(email)
        };

        props.dispatch(preConsentFormSubmissions(props.slug, sanitizedData))
    };

    let backToSelectionBtnText = props.theme.text.backToSelectionBtn;
    let requireWinz = false;

    const onCancel = () => {
        setFormValues({});
        props.dispatch({ type: 'LOGIN_PROCESS_CANCELLED' });
    }

    return (
        <form onSubmit={handleSubmit} className="consent_form">
            <div className="form-group">
                <h2>Please provide your details below:</h2>
            </div>

            {formErrors.length > 0 && (
                <div className="form-group error-banner">
                    <div className="warning">Warning: Please review and correct the details below:</div>
                    <ul className="error-list">
                        {formErrors.map((err, idx) => (
                            <li key={idx} className="error">{err}</li>
                        ))}
                    </ul>
                </div>
            )}
            
            {fields.map((field, index) => (
                <div className="form-group" key={`${field.name}-${index}`}>
                    <label>{field.label}:</label>
                    <input
                        type={field.type}
                        name={field.name}
                        id={field.id}
                        autoComplete={field.autocomplete}
                        required={field.required}
                        value={formValues[field.name] || ''}
                        onChange={handleChange}
                        className="form-control"
                        maxLength={field.maxLength || 50}
                        {...(field.pattern && { pattern: field.pattern })}
                        title={field.title ? field.title : 'Please fill out this field.'}
                    />
                </div>                   
            ))}

            <div className="form-group">
                <p className="acknowledgement">{acknowledgement}</p>
            </div>
            <div className="form-group">
                <button type="submit" className="form-control btn btn-primary">Continue</button>
            </div>
            <CancelLogin action={onCancel} theme={props.theme} backToSelectionBtnText={backToSelectionBtnText} requireWinz={requireWinz}/>
        </form>
    );
    
};

export default PreConsentForm;
